/**
 * The blackjack table's flights: which chips and cards move where, for each step.
 *
 * The flight LAYER is poker's (`flight.tsx`) — the copies, the arcs, the settle, the
 * reduced-motion fades, one `PlayingCard` and one `ChipStacks`. What differs is where
 * things come from and go to, so this is only a planner: a card leaves the SHOE rather
 * than the deck, a bet goes into a CIRCLE, a payout comes out of the house's brass tray
 * and a losing bet goes back into it, and at the end of a hand every card goes to the
 * discard tray before the tray empties back into the shoe. Every anchor is a `data-td`
 * attribute `BlackjackTable` sets for exactly this, so no class name is load-bearing.
 */
import { SHAPE, type Durations, type BjDurations } from "./pace";
import { breakdown } from "./ChipStacks";
import { centre, point, widthOf, lengthVar, flightId, type Flight, type Point } from "./flight";
import type { BjStep } from "./blackjack-director";
import type { BjView } from "./blackjack-view";
import { CARD_STEP_X, cardStepFor } from "../lib/blackjack-geometry";

/** Bet units are 25-Buck chips. */
const UNIT = 25;
/** The stagger a hand's cards lie at, as fractions of a card — `blackjack.css` `.bk-cardslot`. */
export const CARD_STEP = { x: CARD_STEP_X, y: 0, heroY: -0.14 } as const;

class BjAnchors {
  readonly origin: DOMRect;
  constructor(private readonly root: HTMLElement) { this.origin = root.getBoundingClientRect(); }
  q(sel: string): Element | null { return this.root.querySelector(sel); }
  at(sel: string): Point | null { return point(this.q(sel), this.origin); }
  shoe(): Point { return this.at("[data-td=deck]") ?? { x: this.origin.width * 0.78, y: this.origin.height * 0.2 }; }
  tray(): Point { return this.at("[data-td=discard]") ?? { x: this.origin.width * 0.22, y: this.origin.height * 0.2 }; }
  house(): Point { return this.at("[data-td=house]") ?? { x: this.origin.width / 2, y: this.origin.height * 0.1 }; }
  chip(): number { return lengthVar(this.q("[data-td=bet]"), "--chip", 24); }
  circle(seat: number, hand: number): Point | null {
    return point(this.q(`[data-td=bet][data-seat="${seat}"][data-hand="${hand}"] .td-chips`), this.origin)
      ?? point(this.q(`[data-td=bet][data-seat="${seat}"][data-hand="0"]`), this.origin);
  }
  ins(seat: number): Point | null { return point(this.q(`[data-td=ins][data-seat="${seat}"] .td-chips`), this.origin); }
  /** Where a seat's money lives: the player's stack number; a seat without one, its face. */
  purse(seat: number): Point | null {
    return centre(this.q(`[data-td=seat][data-seat="${seat}"] [data-td=stacknum]`), this.origin)
      ?? centre(this.q(`[data-td=seat][data-seat="${seat}"] [data-td=av]`), this.origin);
  }
  /** A hand's element: the player's big hand, a seat's row on the felt, or the dealer's. */
  handEl(seat: number, hand: number, hero: boolean): Element | null {
    if (seat < 0) return this.q("[data-td=dealerhand]");
    return hero
      ? this.q(`[data-td=hand][data-hand="${hand}"]`)
      : this.q(`[data-td=bjhand][data-seat="${seat}"][data-hand="${hand}"]`);
  }
  /** Where card `index` of a hand will lie, and how wide it is. */
  slot(seat: number, hand: number, index: number, hero: boolean): { at: Point; width: number } | null {
    const el = this.handEl(seat, hand, hero);
    if (!el) return null;
    const cards = [...el.querySelectorAll(".gc")];
    const declared = parseFloat(getComputedStyle(el).getPropertyValue(hero ? "--hero" : "--bk-card"));
    const w = cards.length ? widthOf(cards[0]!, 30) : (Number.isFinite(declared) && declared > 0 ? declared : 30);
    const h = w / 0.741;
    const first = cards[0] ? centre(cards[0], this.origin) : null;
    const box = point(el, this.origin);
    const base = first ?? (box ? { x: box.x + (cards.length ? 0 : w / 2), y: box.y } : null);
    if (!base) return null;
    /* The player's hand lifts each card on a phone and lies flat under the plate on a
       desktop (`--lift: 0`, Phase 9 box 0(3)); the flight reads which from the row. */
    const lift = hero ? parseFloat(getComputedStyle(el).getPropertyValue("--lift")) : CARD_STEP.y;
    const dy = Number.isFinite(lift) ? lift : hero ? CARD_STEP.heroY : CARD_STEP.y;
    return { at: { x: base.x + index * cardStepFor(index + 1) * w, y: base.y + index * dy * h }, width: w };
  }
  /** A card at felt size: any seat's on the felt, else one of the shoe's own backs. */
  feltCard(): number | null {
    const c = this.q("[data-td=bjhand] .gc") ?? this.q(".bk-shoe-card .gc");
    return c ? widthOf(c, 30) : null;
  }
  /** Every card on the table, for the collect. */
  allCards(): { at: Point; width: number }[] {
    return [...this.root.querySelectorAll("[data-td=bjhand] .gc, [data-td=dealerhand] .gc, [data-td=hand] .gc")]
      .map((c) => ({ at: centre(c, this.origin), width: widthOf(c, 30) }))
      .filter((x): x is { at: Point; width: number } => x.at !== null);
  }
}

const dist = (p: Point, q: Point) => Math.hypot(q.x - p.x, q.y - p.y);

export function bjFlights(step: BjStep, before: BjView, root: HTMLElement, d: Durations): Flight[] {
  const a = new BjAnchors(root);
  const fade = d.reduced;
  const out: Flight[] = [];
  const mk = (f: Omit<Flight, "id" | "fade" | "delay" | "swell" | "arc"> & Partial<Pick<Flight, "delay" | "swell" | "arc">>): Flight =>
    ({ id: flightId(), fade, delay: 0, swell: 1, arc: 0, ...f });
  const chips = (from: Point | null, to: Point | null, bucks: number, dur: number, extra: Partial<Flight> = {}) => {
    if (!from || !to || bucks <= 0) return;
    out.push(mk({ kind: "chips", from, to, width: a.chip(), dur, stacks: breakdown(bucks),
                  swell: SHAPE.betScale, arc: dist(from, to) * SHAPE.betArc, ...extra }));
  };
  const isHero = (seat: number) => !!before.seats[seat]?.isHero;

  switch (step.kind) {
    case "collect": {
      const tray = a.tray();
      a.allCards().forEach((c, i) => out.push(mk({
        kind: "card", from: c.at, to: tray, width: c.width, dur: d.potCollect, delay: i * d.collectStagger / 2,
        rotateTo: 0, muck: true, swell: SHAPE.muckEndScale,
      })));
      break;
    }
    case "shuffle": {
      const from = a.tray(), to = a.shoe();
      for (let i = 0; i < 3; i++) {
        out.push(mk({ kind: "card", from, to, width: 24, dur: d.shuffleSplit + d.riffle, delay: i * d.riffle, rotateTo: 0,
                      arc: dist(from, to) * SHAPE.betArc }));
      }
      break;
    }
    case "bet":
    case "chips":
      chips(a.purse(step.seat), a.circle(step.seat, step.kind === "chips" ? step.hand : 0), step.amount * UNIT, step.land || d.betSlide);
      break;
    case "insure":
      chips(a.purse(step.seat), a.ins(step.seat), step.amount * UNIT, step.land || d.betSlide);
      break;
    case "deal": {
      const hero = step.seat >= 0 && isHero(step.seat);
      const target = a.slot(step.seat, step.hand, step.index, hero);
      if (!target) break;
      const from = a.shoe();
      /* The player's card leaves the shoe at a felt card's size and grows into the big hand.
         This asked for seat 0's row on the felt, which the player does not have, so it
         always found nothing and the card flew at full size from the shoe. */
      const seatCard = hero ? a.feltCard() ?? target.width : target.width;
      out.push(mk({
        kind: "card", from, to: target.at, width: target.width, dur: step.land,
        rotateTo: 0, arc: dist(from, target.at) * SHAPE.betArc,
        growFrom: hero ? Math.min(1, seatCard / Math.max(1, target.width)) : undefined,
      }));
      break;
    }
    case "split": {
      const hero = isHero(step.seat);
      const from = a.slot(step.seat, step.hand, 1, hero);
      if (!from) break;
      out.push(mk({
        kind: "card", from: from.at, to: { x: from.at.x + from.width * 1.4, y: from.at.y }, width: from.width,
        dur: step.dur, rotateTo: 0,
      }));
      break;
    }
    case "settle": {
      const circle = a.circle(step.seat, step.hand);
      const purse = a.purse(step.seat);
      const bet = before.seats[step.seat]?.hands[step.hand]?.bet ?? 0;
      if (step.returned > 0) {
        /* The stake comes back out of the circle; what it made comes out of the house. */
        chips(circle, purse, Math.min(step.returned, bet) * UNIT, step.land, { swell: SHAPE.pushScale });
        if (step.net > 0) chips(a.house(), purse, step.net * UNIT, step.land, { swell: SHAPE.pushScale, delay: (d as Partial<BjDurations>).settleStagger ?? 0 });
      } else {
        chips(circle, a.house(), bet * UNIT, step.land);
      }
      break;
    }
    case "insurance": {
      const ins = a.ins(step.seat);
      const purse = a.purse(step.seat);
      const amount = before.seats[step.seat]?.insurance ?? 0;
      if (step.won) chips(a.house(), purse, step.returned * UNIT, step.land, { swell: SHAPE.pushScale });
      else chips(ins, a.house(), amount * UNIT, step.land);
      break;
    }
    default:
      break;
  }
  return out;
}
